import axios from "axios";
import axiosInstance from "@/services/https/global";
import CONFIG from "@/config";

const baseUrl = `${CONFIG.BASE_URL}/audit-reporting/api/v1/reports`;

const throwAxios = (error) => {
  if (axios.isAxiosError(error)) {
    throw error.response?.data || error;
  }
  throw new Error("An unexpected error occurred");
};

// List response is NOT wrapped in the usual {status,code,message,data} envelope -
// it comes back as {data, totalCount, pageSize, currentPage, hasNext} directly.
export const getReports = async (params = {}) => {
  const queryParams = {};

  if (params.page !== undefined) queryParams.page = params.page;
  if (params.size !== undefined) queryParams.size = params.size;
  if (params.sort) queryParams.sort = params.sort;
  if (params.type) queryParams.type = params.type;
  if (params.status) queryParams.status = params.status;
  if (params.createdById) queryParams.createdById = params.createdById;
  if (params.search && params.search.trim())
    queryParams.search = params.search.trim();

  try {
    const response = await axiosInstance.get(baseUrl, { params: queryParams });
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const getReportById = async (id) => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/${id}`);
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const getReportOptions = async () => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/options`);
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const createReport = async (payload) => {
  try {
    const response = await axiosInstance.post(baseUrl, payload);
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const updateReport = async ({ id, payload }) => {
  try {
    const response = await axiosInstance.put(`${baseUrl}/${id}`, payload);
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

// responseType "blob" applies to error responses too, so a JSON error body
// (e.g. "report not ready yet") arrives as an opaque Blob instead of parsed
// JSON. Decode it back so the real backend message isn't lost.
const parseBlobError = async (data) => {
  try {
    return JSON.parse(await data.text());
  } catch {
    return null;
  }
};

// Streams the file body directly (no {status,code,message,data} envelope),
// so the full axios response is returned - callers need response.data (the
// blob) and response.headers for the filename/content-type.
export const downloadReport = async (id) => {
  try {
    const response = await axiosInstance.get(`${baseUrl}/${id}/download`, {
      responseType: "blob",
    });
    return response;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const parsed = await parseBlobError(error.response.data);
      if (parsed) throw parsed;
    }
    return throwAxios(error);
  }
};

export const deleteReport = async (id) => {
  try {
    const response = await axiosInstance.delete(`${baseUrl}/${id}`);
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const approveReport = async ({ id, note }) => {
  try {
    const response = await axiosInstance.post(`${baseUrl}/${id}/approve`, {
      note,
    });
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};

export const rejectReport = async ({ id, note }) => {
  try {
    const response = await axiosInstance.post(`${baseUrl}/${id}/reject`, {
      note,
    });
    return response.data;
  } catch (error) {
    return throwAxios(error);
  }
};
